import React, { useEffect, useRef } from "react";

import { useWhitelistingPausedRedirect,WHITELISTING_PAUSED_HINT } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { closeCart, refreshCartCovers, removeFromCart, setCheckoutStep,updateQuantity } from "@store/reducers/cart";

import styled from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";

import { BottomFade, ScrollArea, ScrollChevron, ScrollHint, useScrollAffordance } from "./phoneTemplate";
import {
  ButtonGroup,
  CartHeader,
  CartItemBottom,
  CartItemInfo,
  CartItemInfoRow,
  CartItemPrice,
  CartItemRow,
  CartItemSub,
  CartItemTitle,
  CartItemTop,
  CartThumb,
  CartThumbWrap,
  DeleteBtn,
  EmptyState,
  PrimaryBtn,
  QtyBtn,
  QtyValue,
  QuantityWrap,
  SlidePanelBody,
  SlidePanelFooter,
  TotalAmount,
  TotalRow,
  TotalsWrap,
  VatNotice,
} from "./styles";

/* Body variant that keeps CartHeader's top-padding (10px desktop / 16px phone)
   even though it's wrapped in ScrollArea (which breaks the header's adjacency
   rule). Lets us add the scroll fade without shifting the cart's spacing. */
const CartScrollBody = styled(SlidePanelBody)`
  padding-top: 10px;
  @media (max-width: 600px) {
    padding-top: 16px;
  }
`;

const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CartView: React.FC = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.cart.items);
  const cartMode = useAppSelector((state) => state.cart.mode);
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;
  const isLoggedInCheckout = useAppSelector((state) => state.cart.isLoggedInCheckout);
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();

  useEffect(() => {
    if (items.length > 0) {
      dispatch(refreshCartCovers());
    }
  }, []);

  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleProceed = () => {
    // Paused account (ToU §17.4): checkout is closed. Close the cart and take
    // them to the Account Issues modal — the greyed button alone left the click
    // looking broken.
    if (whitelistingSuspended) {
      dispatch(closeCart());
      return goFixWhitelisting();
    }
    // Skip the standalone "Account" step entirely. Authenticated users go
    // straight to the next functional step. Guests still need to log in /
    // sign up — they get routed through the CheckoutView (which now functions
    // purely as a login/signup gate, not an account-confirmation step).
    if (isAuthenticated || isLoggedInCheckout) {
      if (cartMode === "subscription") dispatch(setCheckoutStep("plan-config"));
      else dispatch(setCheckoutStep("tip"));
    } else {
      dispatch(setCheckoutStep("checkout"));
    }
  };

  if (items.length === 0) {
    return (
      <SlidePanelBody style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <EmptyState>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z"
              stroke="#b9a089"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.4"
            />
            <path d="M3 6H21" stroke="#b9a089" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
          </svg>
          <p>Your cart is empty</p>
        </EmptyState>
      </SlidePanelBody>
    );
  }

  return (
    <>
      <CartHeader>
        <CartIcon />
        <h2>Cart</h2>
      </CartHeader>

      <ScrollArea>
      <CartScrollBody ref={bodyRef}>
      {items.map((item, index) => {
        const qtyPrice = (
          <>
            <QuantityWrap>
              <QtyBtn onClick={() => dispatch(updateQuantity({ index, change: -1 }))}>-</QtyBtn>
              <QtyValue>{item.quantity}</QtyValue>
              <QtyBtn onClick={() => dispatch(updateQuantity({ index, change: 1 }))}>+</QtyBtn>
            </QuantityWrap>
            <CartItemPrice>${(item.price * item.quantity).toFixed(2)}</CartItemPrice>
          </>
        );
        return (
          <CartItemRow key={`${item.trackId}-${item.licenseType}`}>
            <CartItemTop>
              <CartThumbWrap>
                <CartThumb
                  src={item.coverUrl}
                  alt={item.trackName}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              </CartThumbWrap>
              <CartItemInfo>
                <CartItemTitle>{item.trackName}</CartItemTitle>
                <CartItemSub>{item.composerName} &middot; {item.licenseLabel}</CartItemSub>
                {/* desktop: qty + price inline, right of the thumbnail */}
                <CartItemInfoRow>{qtyPrice}</CartItemInfoRow>
              </CartItemInfo>
              <DeleteBtn onClick={() => dispatch(removeFromCart(index))}>
                <TrashIcon />
              </DeleteBtn>
            </CartItemTop>
            {/* phone: qty + price on their own row below */}
            <CartItemBottom>{qtyPrice}</CartItemBottom>
          </CartItemRow>
        );
      })}

      <TotalsWrap>
        <TotalRow $bold style={{ justifyContent: "flex-end" }}>
          <TotalAmount $bold>${subtotal.toFixed(2)}</TotalAmount>
        </TotalRow>
        <VatNotice style={{ marginTop: "-1px" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "translateY(-1px)" }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>VAT will be calculated at checkout</span>
        </VatNotice>
      </TotalsWrap>
      </CartScrollBody>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <ButtonGroup style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
          <PrimaryBtn onClick={handleProceed} title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}><ButtonLabel glyph="arrowRight">Proceed to Checkout</ButtonLabel></PrimaryBtn>
        </ButtonGroup>
      </SlidePanelFooter>
    </>
  );
};

export default CartView;
